/**
 * Limited Access Mode helpers.
 * Mirrors backend `resolveUserAccess()` payload from login / access-context / assign-artist.
 *
 * @typedef {Object} AccessArtist
 * @property {string|null} activeArtistProfileId
 * @property {string|null} songstatsArtistId
 * @property {'not_linked'|'low_data'|'full'|string|null} dataState
 * @property {boolean} hasArtistLink
 *
 * @typedef {Object} AccessCapabilities
 * @property {boolean} canViewRealAnalytics
 * @property {boolean} canUseDataAnalystData
 * @property {boolean} canUseAmplitudeDataMode
 * @property {boolean} canUseAmplitudeSuggestions
 * @property {boolean} canUseAmplitudeChat
 * @property {boolean} canUseUrStudio
 * @property {boolean} canUseProfessorU
 * @property {boolean} canUseGrowthEngineCampaigns
 * @property {boolean} canUseGrowthEngineAutoLinks
 * @property {boolean} canUseLinksFanHubData
 * @property {boolean} canUseArtistManagerDspMetrics
 * @property {boolean} canUseFinancialPlannerDspEstimates
 *
 * @typedef {Object} UserAccess
 * @property {'full'|'limited'} accessMode
 * @property {'no_profile'|'low_data'|'non_subscriber'|null} limitedReason
 * @property {AccessCapabilities} capabilities
 * @property {boolean} hasActiveSubscription
 * @property {AccessArtist} artist
 * @property {Object|null} [creditsPreview]
 */

/** Capability keys returned by the backend access resolver. */
export const ACCESS_CAPABILITIES = Object.freeze({
    canViewRealAnalytics: "canViewRealAnalytics",
    canUseDataAnalystData: "canUseDataAnalystData",
    canUseAmplitudeDataMode: "canUseAmplitudeDataMode",
    canUseAmplitudeSuggestions: "canUseAmplitudeSuggestions",
    canUseAmplitudeChat: "canUseAmplitudeChat",
    canUseUrStudio: "canUseUrStudio",
    canUseProfessorU: "canUseProfessorU",
    canUseGrowthEngineCampaigns: "canUseGrowthEngineCampaigns",
    canUseGrowthEngineAutoLinks: "canUseGrowthEngineAutoLinks",
    canUseLinksFanHubData: "canUseLinksFanHubData",
    canUseArtistManagerDspMetrics: "canUseArtistManagerDspMetrics",
    canUseFinancialPlannerDspEstimates: "canUseFinancialPlannerDspEstimates",
});

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const isLimited = (access) => access?.accessMode === "limited";

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const isFullAccess = (access) => access?.accessMode === "full";

/**
 * @param {UserAccess|null|undefined} access
 * @returns {'no_profile'|'low_data'|'non_subscriber'|null}
 */
export const getLimitedReason = (access) => access?.limitedReason ?? null;

/**
 * Artist linked for Songstats-backed features.
 * Uses access payload first; optional legacy userDetails fallback during migration.
 *
 * @param {UserAccess|null|undefined} access
 * @param {{ assignedArtistSongstateId?: string }|null|undefined} [userDetails]
 * @returns {boolean}
 */
export const hasArtistLinked = (access, userDetails = null) => {
    if (access?.artist?.hasArtistLink === true) return true;
    if (access?.artist?.songstatsArtistId) return true;
    if (userDetails?.assignedArtistSongstateId) return true;
    return false;
};

/**
 * @param {UserAccess|null|undefined} access
 * @param {keyof typeof ACCESS_CAPABILITIES} capability
 * @returns {boolean}
 */
export const can = (access, capability) => {
    if (!capability) return false;
    return access?.capabilities?.[capability] === true;
};

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const canViewRealAnalytics = (access) =>
    can(access, ACCESS_CAPABILITIES.canViewRealAnalytics);

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const canUseAmplitudeChat = (access) =>
    can(access, ACCESS_CAPABILITIES.canUseAmplitudeChat);

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const canUseAmplitudeSuggestions = (access) =>
    can(access, ACCESS_CAPABILITIES.canUseAmplitudeSuggestions);

/**
 * Show real Amplitude suggestions when full access or billing confirms Pro + artist
 * (handles stale persisted access after Stripe checkout).
 *
 * @param {UserAccess|null|undefined} access
 * @param {{ assignedArtistSongstateId?: string }|null|undefined} [userDetails]
 * @param {{ hasActiveSubscription?: boolean }|null|undefined} [billing]
 * @returns {boolean}
 */
export const shouldShowAmplitudeSuggestions = (access, userDetails = null, billing = null) => {
    if (canUseAmplitudeSuggestions(access)) return true;

    const subscribed =
        access?.hasActiveSubscription === true || billing?.hasActiveSubscription === true;
    if (!subscribed) return false;

    return hasArtistLinked(access, userDetails);
};

/**
 * Whether GET /user/v1/get-artist-info should run (Songstats-backed profile).
 * Requires artist linked and active Pro subscription.
 *
 * @param {UserAccess|null|undefined} access
 * @param {{ assignedArtistSongstateId?: string }|null|undefined} [userDetails]
 * @param {{ hasActiveSubscription?: boolean }|null|undefined} [billing]
 * @returns {boolean}
 */
export const shouldFetchArtistInfo = (access, userDetails = null, billing = null) => {
    if (!hasArtistLinked(access, userDetails)) return false;

    const subscribed =
        access?.hasActiveSubscription === true || billing?.hasActiveSubscription === true;
    return subscribed;
};

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const canUseDataAnalystData = (access) =>
    can(access, ACCESS_CAPABILITIES.canUseDataAnalystData);

/**
 * Whether Songstats artist info/stats APIs should be called.
 *
 * @param {UserAccess|null|undefined} access
 * @param {{ assignedArtistSongstateId?: string }|null|undefined} [userDetails]
 * @returns {boolean}
 */
export const shouldFetchArtistAnalytics = (access, userDetails = null) =>
    hasArtistLinked(access, userDetails) && canViewRealAnalytics(access);

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const canUseLinksFanHubData = (access) =>
    can(access, ACCESS_CAPABILITIES.canUseLinksFanHubData);

/**
 * Limited Access users can run Growth Engine campaigns but cannot use Feature.fm.
 */
export const canUseGrowthEngineAutoLinks = (access) =>
    can(access, ACCESS_CAPABILITIES.canUseGrowthEngineAutoLinks);

export const canUseFeatureFmCampaignInsights = (access, userDetails = null, billing = null) => {
    if (canUseLinksFanHubData(access) || canUseGrowthEngineAutoLinks(access)) return true;
    if (isLimited(access)) return false;
    if (isFullAccess(access)) return true;
    return hasFullLinksFanHubAccess(access, userDetails, billing);
};

/**
 * Full Links & Fan Hub access (builders, edits, fan engage).
 * Uses access capability first; billing fallback when access is not hydrated yet.
 *
 * @param {UserAccess|null|undefined} access
 * @param {{ assignedArtistSongstateId?: string }|null|undefined} [userDetails]
 * @param {{ hasActiveSubscription?: boolean }|null|undefined} [billing]
 * @returns {boolean}
 */
export const hasFullLinksFanHubAccess = (access, userDetails = null, billing = null) => {
    if (canUseLinksFanHubData(access)) return true;

    if (!access && hasArtistLinked(access, userDetails)) {
        const subscribed =
            billing?.hasActiveSubscription === true;
        return subscribed;
    }

    return false;
};

/**
 * @param {UserAccess|null|undefined} access
 * @returns {boolean}
 */
export const canUseFinancialPlannerDspEstimates = (access) =>
    can(access, ACCESS_CAPABILITIES.canUseFinancialPlannerDspEstimates);

/**
 * DSP / AI estimates on Release & Financial Planner (forecast graph, stream estimates, highest ROI insight).
 *
 * @param {UserAccess|null|undefined} access
 * @param {{ assignedArtistSongstateId?: string }|null|undefined} [userDetails]
 * @param {{ hasActiveSubscription?: boolean }|null|undefined} [billing]
 * @returns {boolean}
 */
export const hasFinancialPlannerDspAccess = (access, userDetails = null, billing = null) => {
    if (canUseFinancialPlannerDspEstimates(access)) return true;

    if (!access && hasArtistLinked(access, userDetails)) {
        return billing?.hasActiveSubscription === true;
    }

    return false;
};

/**
 * Hybrid greeting (Option 1): artist name when linked, otherwise account owner name.
 *
 * @param {{ firstName?: string, lastName?: string, userName?: string, email?: string, assignedArtistSongstateId?: string }|null|undefined} userDetails
 * @param {{ name?: string }|null|undefined} artistInfo
 * @param {UserAccess|null|undefined} [access]
 * @returns {string}
 */
export const getUserDisplayName = (userDetails, artistInfo, access = null) => {
    const user = userDetails || {};
    const fullName = [user.firstName, user.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ");

    const userLabel =
        fullName ||
        user.userName?.trim() ||
        (user.email?.trim() ? user.email.split("@")[0] : "") ||
        "there";

    const artistName = artistInfo?.name?.trim();
    if (hasArtistLinked(access, userDetails) && artistName) {
        return artistName;
    }

    return userLabel;
};
